import React, { lazy, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import { Element } from 'react-scroll';

import { findValidDelivery } from 'Redux/ducks/deliveries';
import { findValidFreeProduct } from 'Redux/ducks/free-product';
import { findValidVoucher } from 'Redux/ducks/vouchers';
import FormatPartOfBundle from 'Helpers/Product/FormatPartOfBundle';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import SiteSlider from 'Components/Utility/SiteSlider/SiteSlider';
import EmailSubscribeOffer from 'Components/ProductOffers/EmailSubscribeOffer'; // This doesn't like to be lazyloaded, it causes the slider to flash
import VoucherDelivery from 'Components/ProductOffers/VoucherDelivery'; // This doesn't like to be lazyloaded, it causes the slider to flash

import 'Components/ProductOffers/ProductOffers.scss';

const ValidCoupon = lazy(() => import(/* webpackChunkName: "ProductOfferBanners" */ 'Components/ProductOffers/ValidCoupon'));
const PartOfBundle = lazy(() => import(/* webpackChunkName: "ProductOfferBanners" */ 'Components/ProductOffers/PartOfBundle'));
const PreorderBonus = lazy(() => import(/* webpackChunkName: "ProductOfferBanners" */ 'Components/ProductOffers/PreorderBonus'));
const EmailSignupPlacement = lazy(() => import(/* webpackChunkName: "ProductOfferBanners" */ 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement'));
const FreeProductContainer = lazy(() => import(/* webpackChunkName: "ProductOfferBanners" */ 'Components/FreeProduct/FreeProductContainer'));
const LinkBannerOffer = lazy(() => import(/* webpackChunkName: "ProductOfferBanners" */ 'Components/ProductOffers/LinkBannerOffer'));

function ProductOffers({
  product,
  deliveries,
  freeProduct,
  vouchers,
  title,
  emailNewsletter,
  competitions,
}) {
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    const competition = competitions[0] || {};
    const delivery = findValidDelivery(product, deliveries);
    const validPromotions = findValidFreeProduct(product, freeProduct, true);
    const validVouchers = findValidVoucher(product, vouchers);
    const partOfBundle = FormatPartOfBundle(
      product.in_bundles,
      product.firstPickAndMixTier,
      product.nextPickAndMixTier,
      product.currentSaveMore,
    );
    const dates = ProductRequirements.ReleaseDates(product);

    const showPreorderBonus = (product.preorder_bonus_banner && dates.isPreorder) || (dates.earlyAdopterBonus && product.preorder_bonus_banner); // eslint-disable-line max-len

    const showLinkBannerOffer = product.slug === 'attack-on-titan-mega-manga-bundle';

    const buildingOffers = [];

    if (showLinkBannerOffer) {
      buildingOffers.push({ type: 'linkBundleBanner', order: buildingOffers.length + 1 });
    }

    if (!isEmpty(validVouchers)) {
      buildingOffers.push({ type: 'voucher', order: buildingOffers.length + 1, offerData: validVouchers });
    }

    if (showPreorderBonus) {
      buildingOffers.push({ type: 'preOrderBonus', order: buildingOffers.length + 1 });
    }

    if (!isEmpty(competition)) {
      buildingOffers.push({ type: 'competition', order: buildingOffers.length + 1 });
    }

    if (!isEmpty(partOfBundle)) {
      buildingOffers.push({ type: 'partOfBundle', order: buildingOffers.length + 1, offerData: partOfBundle });
    }

    if (!isEmpty(validPromotions)) {
      validPromotions.forEach((vp) => {
        buildingOffers.push({ type: 'freeProductTrigger', order: buildingOffers.length + 1, offerData: vp });
      });
    }

    if (delivery) {
      buildingOffers.push({ type: 'voucherDeliveryTrigger', order: buildingOffers.length + 1 });
    }

    if (!emailNewsletter && isEmpty(competition)) {
      buildingOffers.push({ type: 'emailNewsletter', order: buildingOffers.length + 1 });
    }

    setOffers(buildingOffers);
  }, [
    competitions,
    emailNewsletter,
    deliveries,
    product,
    freeProduct,
    vouchers,
  ]);

  if (!product || product?.presale || product?.isPreview) return false;

  if (isEmpty(offers)) return false;

  const sliderSettings = {
    xs: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    sm: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    md: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    lg: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
    xl: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
    xxl: {
      slidesToShow: 2,
      slidesToScroll: 1,
    },
  };

  let sectionTitle = <FormattedMessage id="newproduct.specialoffers.title" defaultMessage="Special Offers" />;
  if (title) sectionTitle = title;

  return (
    <section data-nosnippet className="product-offers-container page-section-margin ProductOffers">
      <Element name="offers">
        <h2 className="section-title pb-2">
          {sectionTitle}
        </h2>
      </Element>

      {offers.length > 0 &&
        <SiteSlider sliderSettings={sliderSettings} sliderClass="ProductOfferCarousel">
          {offers.map(offer =>
            <div className="ProductOfferCarousel__offerContainer" key={offer.order}>
              {offer.type === 'linkBundleBanner' &&
                <LinkBannerOffer />
              }
              {offer.type === 'voucher' &&
                <ValidCoupon voucher={orderBy(offer.offerData, 'percent', 'desc')[0]} />
              }
              {offer.type === 'preOrderBonus' &&
                <PreorderBonus product={product} />
              }
              {offer.type === 'freeProductTrigger' &&
                <FreeProductContainer
                  product={product}
                  promotion={offer.offerData}
                />
              }
              {offer.type === 'partOfBundle' &&
                <PartOfBundle partOfBundle={offer.offerData} productName={product.name} />
              }
              {offer.type === 'competition' &&
                <EmailSignupPlacement appearsOn="product_offers" />
              }
              {offer.type === 'emailNewsletter' &&
                <EmailSubscribeOffer />
              }
              {offer.type === 'voucherDeliveryTrigger' &&
                <VoucherDelivery product={product} />
              }
            </div>,
          )}
        </SiteSlider>
      }
    </section>
  );
}

ProductOffers.propTypes = {
  product: PropTypes.shape({}),
  deliveries: PropTypes.arrayOf(PropTypes.shape({})),
  freeProduct: PropTypes.arrayOf(PropTypes.shape({})),
  vouchers: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
};

ProductOffers.defaultProps = {
  product: {},
  deliveries: [],
  freeProduct: [],
  vouchers: [],
  title: '',
};

const mapStateToProps = ({
  deliveries: { all: deliveriesAll },
  freeProduct,
  vouchers: { all: vouchersAll },
  auth: { email_newsletter: emailNewsletter },
  competitions,
}) => ({
  deliveries: deliveriesAll,
  freeProduct,
  vouchers: vouchersAll,
  emailNewsletter,
  competitions,
});

export default connect(mapStateToProps, {})(ProductOffers);

